/**
 * Lối Ngọc tutorials, shown the first time a level with that id opens. The
 * first levels teach one control each; every chapter's first level
 * introduces its new idea. `focus` tells the scene (floor, satchel, gold
 * line, slabs, doubles, locks, mirrors, face-down tiles, throne) or the
 * HUD (undo, hint) what to highlight; "demo" keeps a sparkle on the next
 * step until the player takes it.
 */
export type TileTutorialFocus =
  | "floor"
  | "demo"
  | "bag"
  | "gold"
  | "stone"
  | "double"
  | "lock"
  | "mirror"
  | "hidden"
  | "throne"
  | "undo"
  | "hint";

export interface TileTutorialStep {
  readonly text: string;
  readonly focus?: TileTutorialFocus;
}

export interface TileTutorial {
  readonly id: string;
  readonly steps: readonly TileTutorialStep[];
}

export const TILE_TUTORIALS: readonly TileTutorial[] = [
  {
    id: "jade-walk",
    steps: [
      { text: "Chào mừng tới Lâu Đài Ngọc! Phù thủy rải gạch lung tung khắp sảnh, mình đi nhặt về giúp lâu đài nha.", focus: "floor" },
      { text: "Chạm ô sát bên để bước tới. Bước lên viên gạch nào là viên đó bay vào túi liền.", focus: "demo" },
    ],
  },
  {
    id: "jade-satchel",
    steps: [
      { text: "Ba viên cùng hình gặp nhau trong túi là ghép thành bùa ngọc rồi bay đi, túi lại rộng chỗ.", focus: "bag" },
      { text: "Túi đầy mà viên mới không ghép được là hết chỗ chứa, bước đó không đi được đâu nha.", focus: "bag" },
    ],
  },
  {
    id: "jade-socket",
    steps: [
      { text: "Ô nhặt xong thành lỗ trống, đi qua rồi là hết quay lại. Tính đường trước kẻo bỏ quên viên nào nha.", focus: "floor" },
      { text: "Lỡ bước thì bấm hoàn tác, miễn phí và bao nhiêu lần cũng được.", focus: "undo" },
    ],
  },
  {
    id: "jade-gold",
    steps: [
      { text: "Thấy cây ghim vàng trên túi không? Giữ túi gọn không vượt vạch vàng là rinh trọn ba sao.", focus: "gold" },
      { text: "Bí quá thì bấm bóng đèn chỉ một bước. Mỗi màn có 2 lượt miễn phí, hết thì đổi vàng lấy thêm.", focus: "hint" },
    ],
  },
  {
    id: "jade-stone",
    steps: [{ text: "Đá lát không bao giờ bị nhặt, đi qua đi lại thoải mái. Chạm viên gạch xa là nhân vật tự đi qua đá tới luôn.", focus: "stone" }],
  },
  {
    id: "jade-double",
    steps: [{ text: "Gạch chồng hai lớp nè! Lần đầu bước lên nhặt lớp trên, lớp dưới khắc hình nhỏ bên hông, ghé lại lần nữa mới lấy được.", focus: "double" }],
  },
  {
    id: "jade-small-bag",
    steps: [{ text: "Túi teo lại còn ít ngăn rồi đó. Gom cho ra bộ ba thật sớm kẻo chật cứng nha.", focus: "bag" }],
  },
  {
    id: "jade-lock",
    steps: [
      { text: "Cửa khóa ngọc đang đóng im ỉm. Hình khắc trên cửa là chìa khóa của nó.", focus: "lock" },
      { text: "Ghép xong một bùa đúng hình đó là cửa tự mở, thành đá lát đi qua thoải mái.", focus: "lock" },
    ],
  },
  {
    id: "jade-mirror",
    steps: [{ text: "Gương thần nè! Bước vào gương này là hiện ra ở gương bên kia, đi qua lại bao nhiêu lần cũng được.", focus: "mirror" }],
  },
  {
    id: "jade-face-down",
    steps: [{ text: "Có mấy viên gạch nằm úp, đứng sát bên thì nó mới chịu lật hình. Chưa biết hình gì thì chừa đường lui nha.", focus: "hidden" }],
  },
  {
    id: "jade-lock-face-down",
    steps: [{ text: "Cửa khóa gặp gạch úp! Lật hình ra rồi mới tính coi chìa nằm ở đâu nha.", focus: "lock" }],
  },
  {
    id: "jade-mirror-double",
    steps: [{ text: "Gương thần gặp gạch chồng, túi lại nhỏ hơn. Ghé gạch chồng hai lần thì nhớ chừa đường về qua gương nha.", focus: "mirror" }],
  },
  {
    id: "jade-throne",
    steps: [
      { text: "Chương kiệt tác! Ngai vàng chỉ mở khi nhặt sạch gạch trong sảnh.", focus: "throne" },
      { text: "Nhặt xong viên cuối thì còn phải đi được tới ngai, nhớ chừa đường lên ngồi nha.", focus: "throne" },
    ],
  },
];

export const TILE_TUTORIAL_BY_ID: ReadonlyMap<string, TileTutorial> = new Map(TILE_TUTORIALS.map((t) => [t.id, t]));
